/*
 * Client-side resolution for the .rdvdeco-delivery-estimate block.
 *
 * SSR Liquid pre-renders the displayable states (in_stock / preorder) with
 * their date range, but cannot read per-location inventory. This script fetches
 * the available quantities per Shopify location through the AECI FastAPI
 * inventory endpoint
 * (GET {apiBaseUrl}/inventory/shopify/variant/{variant_id},
 * base URL injected by js-variables.liquid from the theme setting
 * aeci_api_base_url) and decides the real state for the quantity carried by
 * the node (data-quantity, 1 when absent; the cart passes the variant total):
 *   - "00 - Yusen Stock" available >= quantity -> in_stock
 *   - otherwise "01 - Transit" available > 0   -> preorder: Shopify sells the
 *     physical stock first and routes the excess to the transit location
 *   - otherwise "00 - Yusen Stock" available > 0 -> in_stock
 * Only positive readings are trusted: this source returns partial, stale and
 * even negative quantities, so it never concludes a stock-out. Hiding the block
 * on an unavailable variant is decided from Shopify's own availability, like the
 * server-side render does.
 * It toggles the pre-rendered state spans and keeps the hidden
 * `properties[Livraison estimée ]` input of the product form in sync so the
 * value captured at add-to-cart matches what checkout will actually do
 * (Shopify location priority ships physical stock first).
 *
 * Pubsub events come from the upstream Sleek theme (window.FoxTheme.pubsub).
 * If the bus is not present the initial hydration still runs; only variant
 * switches lose live updates.
 */

(function () {
  'use strict';

  window.rdvdeco = window.rdvdeco || {};

  var ESTIMATE_SELECTOR = '[data-rdvdeco-delivery-estimate]';
  var PROPERTY_SELECTOR = 'input[data-rdvdeco-delivery-property]';
  var STOCK_LOCATION = '00 - Yusen Stock';
  var TRANSIT_LOCATION = '01 - Transit';

  var stockRequests = {};
  var hydratedNodes = new WeakSet();
  var unsubscribers = [];
  var observer = null;
  var scanScheduled = false;

  function apiBaseUrl() {
    var config = window.rdvdeco.config || {};
    var base = config.apiBaseUrl || 'https://fastapi.aeci.pro';
    return base.replace(/\/+$/, '');
  }

  function fetchJson(url) {
    return fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('HTTP ' + response.status + ' on ' + url);
      }
      return response.json();
    });
  }

  function fetchInventoryStock(variantId, sku) {
    if (!variantId && !sku) {
      return Promise.resolve(null);
    }
    var key = String(variantId || '') + '|' + String(sku || '');
    if (!stockRequests[key]) {
      var variantUrl =
        apiBaseUrl() + '/inventory/shopify/variant/' + encodeURIComponent(String(variantId));
      var skuUrl = apiBaseUrl() + '/inventory/shopify/sku/' + encodeURIComponent(String(sku));
      var request;
      if (!variantId) {
        request = fetchJson(skuUrl);
      } else if (!sku) {
        request = fetchJson(variantUrl);
      } else {
        // Variant ids only exist on the shop the API instance is bound to; on
        // the test shop the theme talks to the staging API, so an unknown id
        // falls back to the SKU lookup shared across shops (one extra call).
        request = fetchJson(variantUrl).catch(function () {
          return fetchJson(skuUrl);
        });
      }
      stockRequests[key] = request.catch(function (error) {
        delete stockRequests[key];
        console.error('rdvdeco delivery estimate: stock fetch failed', error);
        return null;
      });
    }
    return stockRequests[key];
  }

  function availableAt(levels, locationName) {
    if (!Array.isArray(levels)) {
      return null;
    }
    for (var i = 0; i < levels.length; i++) {
      var level = levels[i];
      if (level && level.location === locationName) {
        return Number(level.available) || 0;
      }
    }
    return null;
  }

  function requestedQuantity(node) {
    var quantity = Number(node.getAttribute('data-quantity'));
    return quantity >= 1 ? quantity : 1;
  }

  function resolveStockState(levels, quantity) {
    var stock = availableAt(levels, STOCK_LOCATION);
    var transit = availableAt(levels, TRANSIT_LOCATION);
    if (stock === null && transit === null) {
      return null;
    }
    if (stock !== null && stock >= quantity) {
      return 'in_stock';
    }
    if (transit > 0) {
      return 'preorder';
    }
    if (stock > 0) {
      return 'in_stock';
    }
    // Seuls les signaux positifs sont exploitables. Cette source renvoie des
    // réponses partielles, périmées, voire négatives : elle a répondu -5 à
    // Yusen pour un produit que Shopify comptait à 122. On n'en conclut donc
    // jamais la rupture — Liquid l'a déjà fait si Shopify considère la variante
    // indisponible, et cette info-là est fiable.
    return null;
  }

  function applyState(node, state) {
    var current = node.getAttribute('data-state');
    var target = node.querySelector('[data-state-label="' + state + '"]');
    if (!target || current === state) {
      return;
    }
    node.setAttribute('data-state', state);
    node.classList.remove('rdvdeco-delivery-estimate--' + current);
    node.classList.add('rdvdeco-delivery-estimate--' + state);
    var labels = node.querySelectorAll('[data-state-label]');
    for (var i = 0; i < labels.length; i++) {
      if (labels[i] === target) {
        labels[i].removeAttribute('hidden');
      } else {
        labels[i].setAttribute('hidden', 'hidden');
      }
    }
  }

  function hydrateEstimate(node) {
    hydratedNodes.add(node);
    var state = node.getAttribute('data-state');
    if (state === 'sold_out' || state === 'back_soon') {
      return;
    }
    var variantId = node.getAttribute('data-variant-id');
    var sku = node.getAttribute('data-sku');
    fetchInventoryStock(variantId, sku).then(function (levels) {
      if (variantId !== node.getAttribute('data-variant-id')) {
        return;
      }
      var resolved = resolveStockState(levels, requestedQuantity(node));
      if (resolved) {
        applyState(node, resolved);
      }
    });
  }

  function hydrateProperty(input) {
    hydratedNodes.add(input);
    var variantId = input.getAttribute('data-variant-id');
    var sku = input.getAttribute('data-sku');
    var preorderValue = input.getAttribute('data-property-preorder');
    var inStockValue = input.getAttribute('data-property-in-stock');
    fetchInventoryStock(variantId, sku).then(function (levels) {
      if (variantId !== input.getAttribute('data-variant-id')) {
        return;
      }
      var resolved = resolveStockState(levels, requestedQuantity(input));
      if (resolved === 'preorder' && preorderValue) {
        input.value = preorderValue;
      } else if (resolved === 'in_stock' && inStockValue) {
        input.value = inStockValue;
      }
    });
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var estimates = scope.querySelectorAll(ESTIMATE_SELECTOR);
    for (var i = 0; i < estimates.length; i++) {
      if (!hydratedNodes.has(estimates[i])) {
        hydrateEstimate(estimates[i]);
      }
    }
    var properties = scope.querySelectorAll(PROPERTY_SELECTOR);
    for (var j = 0; j < properties.length; j++) {
      if (!hydratedNodes.has(properties[j])) {
        hydrateProperty(properties[j]);
      }
    }
  }

  function scheduleScan() {
    if (scanScheduled) {
      return;
    }
    scanScheduled = true;
    window.requestAnimationFrame(function () {
      scanScheduled = false;
      scan(document);
    });
  }

  function nodesForProduct(selector, productId) {
    if (!productId) {
      return [];
    }
    var nodes = document.querySelectorAll(
      selector + '[data-product-id="' + String(productId) + '"]'
    );
    return Array.prototype.slice.call(nodes);
  }

  function onVariantChange(payload) {
    var data = (payload && payload.data) || payload;
    var variant = data && data.variant;
    if (!variant || !variant.id) {
      return;
    }
    var productId = variant.product_id;

    nodesForProduct(ESTIMATE_SELECTOR, productId).forEach(function (node) {
      node.setAttribute('data-variant-id', String(variant.id));
      if (variant.sku) {
        node.setAttribute('data-sku', variant.sku);
      }
      if (variant.available === false) {
        // Shopify est la seule source fiable de disponibilité : sur une variante
        // indisponible, le message du bloc inventaire suffit et l'estimation
        // ferait doublon. Même règle que le rendu serveur.
        var daysBack = Number(node.getAttribute('data-days-back-in-stock')) || 0;
        node.setAttribute('data-state', daysBack > 0 ? 'back_soon' : 'sold_out');
        node.setAttribute('hidden', 'hidden');
        return;
      }
      node.removeAttribute('hidden');
      var current = node.getAttribute('data-state');
      if (current === 'sold_out' || current === 'back_soon') {
        applyState(node, 'in_stock');
      }
      hydratedNodes.delete(node);
      hydrateEstimate(node);
    });

    nodesForProduct(PROPERTY_SELECTOR, productId).forEach(function (input) {
      input.setAttribute('data-variant-id', String(variant.id));
      if (variant.sku) {
        input.setAttribute('data-sku', variant.sku);
      }
      if (variant.available !== false) {
        hydrateProperty(input);
      }
    });
  }

  function getPubSub() {
    var theme = window.FoxTheme;
    if (!theme || !theme.pubsub) {
      return null;
    }
    return theme.pubsub;
  }

  function subscribe(eventKey, handler) {
    var pubsub = getPubSub();
    if (!pubsub || !pubsub.PUB_SUB_EVENTS || typeof pubsub.subscribe !== 'function') {
      return;
    }
    var eventName = pubsub.PUB_SUB_EVENTS[eventKey];
    if (!eventName) {
      return;
    }
    var unsubscribe = pubsub.subscribe(eventName, handler);
    if (typeof unsubscribe === 'function') {
      unsubscribers.push(unsubscribe);
    }
  }

  function init() {
    scan(document);
    subscribe('variantChange', onVariantChange);
    if ('MutationObserver' in window) {
      observer = new MutationObserver(scheduleScan);
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function teardown() {
    while (unsubscribers.length) {
      var fn = unsubscribers.pop();
      try {
        fn();
      } catch (error) {
        // No-op: a stale unsubscribe should never break the page.
      }
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  window.rdvdeco.inventoryStock = fetchInventoryStock;
  window.rdvdeco.deliveryEstimate = {
    init: init,
    teardown: teardown,
    scan: scan,
    applyState: applyState
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
